import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ResponsiveService } from '@frk/eds-components';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { StateDropdownItems } from '@literature/const/literature-data-const';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { IUserProfile } from '@services/profile.interface';
import { EMAIL_PATTERN } from '../const/literature-data-const';
import { TranslateService } from '@shared/translate/translate.service';
import { SH } from '@utils/app.constants';
import { LiteratureDocument, MailFormValues } from '@types';
import { Logger } from '@utils/logger';
import { StorageService } from '@services/storage.service';

const logger = Logger.getLogger('LiteratureYourCartMailFormComponent');

// we use 4 values to avoid appending/updating session value
const formSessionKeyLoggedClient = 'mailFormLoggedClient_autosave';
const formSessionKeyLoggedMe = 'mailFormLoggedMe_autosave';
const formSessionKeyNotLogged = 'mailFormNotLogged_autosave';
const formSaveSessionActiveClientTypeKey = 'mailFormClientType';
@Component({
  selector: 'ft-literature-your-cart-mail-form',
  templateUrl: './literature-your-cart-mail-form.component.html',
  styleUrls: ['./literature-your-cart-mail-form.component.scss'],
})
export class LiteratureYourCartMailFormComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() cartState: LiteratureDocument[] = [];
  @Output() mailFormValues = new EventEmitter<MailFormValues>();
  @Output() yourCartPage = new EventEmitter();
  isHandheld$: Observable<boolean>;
  isMobile$: Observable<boolean>;
  dropdownItems = StateDropdownItems;
  mailDetails: FormGroup;
  mailDetailsValue: MailFormValues = { deliverTo: 'me' };
  profile$: Observable<IUserProfile>;
  profileData: IUserProfile;
  showError = false;
  clientUseMsg: string;
  isClientUseNo = false;
  showAlert = false;
  alertTitle: string;
  showBlueSkyAlert = false;
  blueSkyStateMsg: string;
  blueSkyStates = new Map<string, LiteratureDocument>();
  prePopulateOnNavigation: MailFormValues;
  isShareHolder = false;

  public clientType$: BehaviorSubject<string> = new BehaviorSubject<string>(
    'me'
  );

  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private responsiveService: ResponsiveService,
    private translateService: TranslateService,
    private literatureDataService: LiteratureDataService,
    private storageService: StorageService
  ) {
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.isMobile$ = this.responsiveService.isMobile$();
  }
  ngOnChanges() {
    // TODO validate with Anurag if we need this after I implemented session storage
    // this.prePopulateOnNavigation = this.literatureDataService.getMailDetails();
    this.checkBlueSkystateRule();
  }

  ngOnInit(): void {
    this.clientUseMsg = this.translateService.instant(
      'literature.not-for-client-use'
    );
    this.alertTitle = this.translateService.instant(
      'literature.attention-required'
    );
    this.mailDetails = new FormGroup({
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      addressLine1: new FormControl('', [Validators.required]),
      addressLine2: new FormControl(''),
      city: new FormControl('', [Validators.required]),
      stateCode: new FormControl('', [
        Validators.required,
        this.checkStateSelected,
      ]),
      zip: new FormControl('', [
        Validators.required,
        Validators.maxLength(10),
        Validators.pattern('^[0-9]*$'),
      ]),
      workPhone: new FormControl('', [
        Validators.required,
        Validators.minLength(12),
      ]),
      emailAddress: new FormControl('', [
        Validators.required,
        Validators.pattern(EMAIL_PATTERN),
      ]),
    });

    this.profile$ = this.literatureDataService.getProfileData$();
    this.profile$
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((profile: IUserProfile) => {
        if (profile?.isLoggedIn) {
          this.profileData = profile;
          // check all rules which is based on login
          this.isShareHolder = SH === this.profileData?.profileInfo?.role;
          this.checkLiteratureRules();
        }
      });
  }

  // prepopulates form data on navigation - button clicked
  // TODO discuss with Anurag if we need that after I implemented session based solution
  public prepopulateOnNavigation() {
    if (this.prePopulateOnNavigation) {
      this.mailDetails.patchValue({
        firstName: this.prePopulateOnNavigation.firstName,
        lastName: this.prePopulateOnNavigation.lastName,
        zip: this.prePopulateOnNavigation.zip,
        city: this.prePopulateOnNavigation.city,
        addressLine1: this.prePopulateOnNavigation.addressLine1,
        emailAddress: this.prePopulateOnNavigation.emailAddress,
        workPhone: this.prePopulateOnNavigation.workPhone,
        stateCode: this.dropdownItems.find(
          (data) => data.value === this.prePopulateOnNavigation.stateCode
        ),
        addressLine2: this.prePopulateOnNavigation?.addressLine2,
      });
    }
  }

  ngAfterViewInit() {
    this.prePopulateWithSession();
    this.saveFormDataWhenChanged();
  }

  // stores form data when modified
  public saveFormDataWhenChanged(): void {
    this.mailDetails.valueChanges
      .pipe(
        debounceTime(1000), // Debounce for 1s (1000 milliseconds)
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        (formValue) => {
          if (this.jsonHasValues(formValue)) {
            const clientType = this.mailDetailsValue.deliverTo;
            const formSaveSessionKey = this.getFormSessionKey(clientType);
            this.saveFormDataInASession(formSaveSessionKey, formValue);
            this.saveClientTypeInASession(clientType);
          }
        },
        () => {
          logger.debug('Form autosave not completed due to storage error');
        }
      );
  }

  public saveFormDataInASession(
    formSaveSessionKey: string,
    formValue: {}
  ): void {
    this.storageService.store(
      formSaveSessionKey,
      JSON.stringify(formValue),
      true
    );
  }

  public saveClientTypeInASession(clientType: string): void {
    this.storageService.store(
      formSaveSessionActiveClientTypeKey,
      JSON.stringify(clientType),
      true
    );
  }

  public prePopulateWithSession() {
    // try to get data from session
    // determine session name ...
    this.updateFormData();

    this.checkBlueSkyState({
      value: this.profileData?.profileInfo?.state,
      label: '',
    });
  }

  // this will be abstract class method
  // method verifies if any value in flat json is set
  // TODO - what about dropdowns when selected empty value?
  public jsonHasValues(jsonObject: {}): boolean {
    try {
      return (
        Object.values(jsonObject)
          .map((elem) => elem.toString().trim())
          .join('') !== ''
      );
    } catch {
      return false;
    }
  }

  // update data from session storage if exists , otherwise profile
  // this will be abstract class method
  public updateFormData(): void {
    let jsonForm = {};

    this.storageService
      .retrieve(formSaveSessionActiveClientTypeKey, true)
      .then((clientType: string) => {
        // set client tape from session variable
        const clientTypeParsed = JSON.parse(clientType) || 'me';
        this.clientType$.next(clientTypeParsed);

        // get session name after checking client type stored in a
        const formSessionKey = this.getFormSessionKey(clientTypeParsed);
        this.mailDetailsValue.deliverTo = clientTypeParsed;

        this.storageService
          .retrieve(formSessionKey, true)
          .then((clientFormData: string) => {
            // set form values if available
            const encodedForm = clientFormData;
            jsonForm = JSON.parse(encodedForm);

            // try to fill from a session
            if (this.jsonHasValues(jsonForm)) {
              this.fillFormWithSessionData(jsonForm);
              return;
            }

            // if no session and no prePopulateOnNavigation try to populate from profile for logged
            if (
              this.profileData?.isLoggedIn &&
              this.mailDetailsValue.deliverTo === 'me'
            ) {
              this.prePopulateFromProfile();
            }
          });
      })
      .catch(() => {
        logger.debug('session form data not available');
      });
  }

  // fills for with session data
  public fillFormWithSessionData(jsonForm: MailFormValues): void {
    this.mailDetails.patchValue({
      firstName: jsonForm.firstName,
      lastName: jsonForm.lastName,
      zip: jsonForm.zip,
      city: jsonForm.city,
      addressLine1: jsonForm.addressLine1,
      emailAddress: jsonForm.emailAddress,
      workPhone: jsonForm.workPhone,
      stateCode: this.dropdownItems.find(
        (data) => data.value === jsonForm?.stateCode?.value
      ),
    });
  }

  /**
   * determines which session key to use
   * @param isMe indicates if form is for client or advisor based on this.mailDetailsValue.deliverTo
   * @returns applicable session key for saved form
   */
  public getFormSessionKey(clientType: string): string {
    const isMe = 'me' === clientType;

    const formSessionKeyLogged = isMe
      ? formSessionKeyLoggedMe
      : formSessionKeyLoggedClient;

    return this.profileData?.isLoggedIn
      ? formSessionKeyLogged
      : formSessionKeyNotLogged;
  }

  /**
   * Initializing the form
   */
  public setFormValues(): void {
    this.showError = true;
    if (this.mailDetails.valid) {
      this.mailDetails.value.stateCode = this.mailDetails.value.stateCode.value;
      this.mailDetailsValue = {
        ...this.mailDetailsValue,
        mailStateCode: this.mailDetails.value.stateCode,
      };
      this.mailDetailsValue = {
        ...this.mailDetailsValue,
        ...this.mailDetails.value,
      };
      if (this.profileData?.isLoggedIn) {
        const userInfo = this.literatureDataService.literatureUserProfileMapper(
          this.profileData?.profileInfo
        );
        this.mailDetailsValue = { ...this.mailDetailsValue, userInfo };
      }
      this.mailFormValues.emit(this.mailDetailsValue);
    } else {
      Object.keys(this.mailDetails.controls).forEach((field) => {
        const control = this.mailDetails.get(field);
        control.markAsTouched({ onlySelf: true });
      });
    }
  }

  /**
   * Emits mailing details
   */
  goToYourCart(): void {
    /*
    TODO verify with Anurag if that is needed or if this was solution to preserve user data in a form?

    this.mailDetails.value.stateCode = this.mailDetails.value?.stateCode.value;
    this.mailDetailsValue = {
      ...this.mailDetailsValue,
      mailStateCode: this.mailDetails.value.stateCode,
    };
    this.mailDetailsValue = {
      ...this.mailDetailsValue,
      ...this.mailDetails.value,
    };
    this.mailFormValues.emit(this.mailDetailsValue);
    */
    this.yourCartPage.emit();
  }

  /**
   * Validates if any state is selected
   */
  checkStateSelected(control: AbstractControl): { [key: string]: any } | null {
    return control?.value?.value === 1
      ? { checkStateSelected: { value: control.value } }
      : null;
  }

  /**
   * Updates if recepient to personal use or client use based on option selected
   */
  public updateRecepient(event: { source: string; value: string }): void {
    if (event.value === '1') {
      this.mailDetailsValue.deliverTo = 'me';
      this.showAlert = false;
      this.saveClientTypeInASession('me');
      this.prePopulateWithSession();
    } else {
      this.showBlueSkyAlert = false;
      this.showAlert = true;
      this.saveClientTypeInASession('myClient');
      this.mailDetailsValue.deliverTo = 'myClient';
      this.mailDetails.reset();

      // try to fill that from from a session
      this.prePopulateWithSession();
      this.mailDetails.patchValue({
        stateCode: { value: 1, label: 'Select State' },
      });
    }

    // save selected clientType in a session
  }

  /**
   * Auto populates details on page load
   */
  public prePopulateFromProfile(): void {
    this.mailDetailsValue.deliverTo = 'me';
    if (!this.profileData?.profileInfo?.displayName) {
      return;
    }
    const fullName = this.profileData?.profileInfo?.displayName?.split(' ');
    this.mailDetails.patchValue({
      firstName: fullName[0],
      lastName: fullName[fullName.length - 1],
      zip: this.profileData?.profileInfo?.zip,
      city: this.profileData?.profileInfo?.city,
      addressLine1: this.profileData?.profileInfo?.addressLine1,
      emailAddress: this.profileData?.profileInfo?.email,
      workPhone: this.profileData?.profileInfo?.phoneNumber,
      stateCode: this.dropdownItems.find(
        (data) => data.value === this.profileData?.profileInfo?.state
      ),
    });
  }

  /**
   * Updates regex for phone number entered
   */
  updatePhoneFormat(data) {
    let newVal = data.replace(/\D/g, '');
    if (newVal.length === 0) {
      newVal = '';
    } else if (newVal.length <= 3) {
      newVal = newVal.replace(/^(\d{0,3})/, '$1');
    } else if (newVal.length <= 6) {
      newVal = newVal.replace(/^(\d{0,3})(\d{0,3})/, '$1-$2');
    } else if (newVal.length <= 10) {
      newVal = newVal.replace(/^(\d{0,3})(\d{0,3})(\d{0,4})/, '$1-$2-$3');
    } else {
      newVal = newVal.substring(0, 10);
      newVal = newVal.replace(/^(\d{0,3})(\d{0,3})(\d{0,4})/, '$1-$2-$3');
    }
    this.mailDetails.patchValue({
      workPhone: newVal,
    });
  }
  checkLiteratureRules() {
    for (const data of this.cartState) {
      if (
        !this.isClientUseNo && // to process only once
        data.clientUse &&
        'No' === data.clientUse &&
        this.profileData?.isLoggedIn
      ) {
        this.isClientUseNo = true;
      }
    }
  }

  /**
   * Updates the blueskystates array for the doc
   */
  checkBlueSkystateRule() {
    for (const data of this.cartState) {
      this.blueSkyStates.set(data.docId, data);
    }
    this.checkBlueSkyState({
      value: this.prePopulateOnNavigation?.stateCode,
      label: '',
    });
  }

  /**
   *
   * @param event selected state with code and label values
   * validates if any blueskystate is associated with doc
   * decides when to show an alert message for selected state
   */
  checkBlueSkyState(event: { value: string; label: string }) {
    // base case when "select state" is selected"
    if (event?.value && event.value !== '1') {
      const msg = [
        `<p>${this.translateService.instant(
          `literature.blue-sky-alert-msg`
        )}</p>`,
      ];
      let isBlueSkyDataAvailable = false;
      for (const entry of this.blueSkyStates.entries()) {
        const data = entry[1];
        if (data.blueSkyStates?.length > 0) {
          isBlueSkyDataAvailable = true;
          if (!data.blueSkyStates.includes(event.value)) {
            msg.push(`<p>${data.fundName} (${data.docId})</p>`);
          }
        }
      }
      if (isBlueSkyDataAvailable && msg.length > 1) {
        this.blueSkyStateMsg = msg.join('');
        this.showBlueSkyAlert = true;
      } else {
        this.showBlueSkyAlert = false;
      }
    }
  }
  translateKey(key: string): string {
    return this.translateService.instant(key);
  }

  /**
   * Custom validation to display error message for each form field
   */
  validatorFunction(requiredFlag: boolean, formFieldName: string): string {
    if (requiredFlag) {
      return this.translateKey(
        `literature.mail-form-error-${formFieldName}-required`
      );
    } else {
      return this.translateKey(
        `literature.mail-form-error-${formFieldName}-data`
      );
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
